/**
 * Artefacts view for a single assessment run.
 *
 *   · Lists artefacts already created for the run.
 *   · Surfaces recommended templates (grouped by type) with a one-click
 *     "Start client copy" button — for Deliverables this mail-merges the
 *     master .docx with engagement data straight away.
 *   · When an artefact is selected, renders the right editor:
 *       – Markdown shapes (Methodology / Runbook / Sector overlay):
 *         split-pane textarea + live sanitised preview.
 *       – Mail-merge shapes (Deliverable .docx): metadata view + buttons to
 *         re-generate, upload an edited version, download current.
 *       – Other binary artefacts: download + upload replacement.
 *   · All status changes (draft/final) and edits append an audit row to
 *     the artefact's `edit_history`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  Download,
  FileText,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
  CheckCircle2,
  Edit3,
  History,
} from "lucide-react";
import { toast } from "sonner";
import { marked } from "marked";
import DOMPurify from "dompurify";
import api, { API_BASE } from "../../lib/api";

marked.setOptions({ gfm: true, breaks: false });

const MARKDOWN_TYPES = new Set(["Methodology", "Runbook", "Sector overlay"]);
const MAIL_MERGE_TYPES = new Set(["Deliverable"]);

const TYPE_ORDER = ["Methodology", "Artefact", "Deliverable", "Runbook", "Sector overlay"];

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const downloadAuthed = async (path, filename) => {
  try {
    const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "artefact";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
};

// ----------------------------------------------------------------------
// Markdown editor (split-pane)
// ----------------------------------------------------------------------

function MarkdownEditor({ value, onChange, onSave, saving }) {
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(value || "")),
    [value]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="artefact-markdown-editor">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase ek-text-muted">Source · markdown</span>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="ek-btn ek-btn-primary text-[11.5px] disabled:opacity-50"
            data-testid="artefact-save"
          >
            <Save size={11} /> {saving ? "Saving…" : "Save draft"}
          </button>
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck
          className="w-full h-[480px] p-3 border ek-line ek-surface font-mono text-[12.5px] leading-relaxed resize-y focus:outline-none focus:border-[#7737F6]"
          data-testid="artefact-markdown-textarea"
        />
      </div>
      <div>
        <div className="mb-1.5">
          <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase ek-text-muted">Preview</span>
        </div>
        <div
          className="h-[480px] p-5 border ek-line ek-surface overflow-y-auto prose prose-sm max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-lg prose-h3:text-base prose-p:text-[13px] prose-li:text-[13px] prose-strong:text-black"
          // html is DOMPurify-sanitized in the useMemo above — safe to render.
          dangerouslySetInnerHTML={{ __html: html }}
          data-testid="artefact-markdown-preview"
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Mail-merge view (Deliverable)
// ----------------------------------------------------------------------

function MailMergeView({ artefact, onChange }) {
  const [busy, setBusy] = useState(false);
  const fileInputRef = React.useRef(null);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/artefacts/${artefact.id}/regenerate`);
      onChange(data);
      toast.success("Mail-merged from the latest run data");
    } catch (e) {
      toast.error(`Re-generate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/artefacts/${artefact.id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      toast.success("File replaced");
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="artefact-mailmerge-view">
      <div className="border ek-line ek-surface p-5">
        <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">Current file</div>
        <div className="flex items-center gap-3 text-[13px]">
          <FileText size={18} className="text-[#7737F6]" />
          <div className="flex-1">
            <div className="font-medium ek-text">{artefact.filename || "—"}</div>
            <div className="text-[11.5px] ek-text-muted">
              {fmtBytes(artefact.size_bytes)} · Mail-merged from master template v{artefact.template_version}
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadAuthed(`/admin/artefacts/${artefact.id}/download`, artefact.filename)}
            className="ek-btn ek-btn-outline text-[12px]"
            data-testid="artefact-download"
          >
            <Download size={12} /> Download
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <button
          type="button"
          onClick={regenerate}
          disabled={busy}
          className="border ek-line ek-surface p-4 text-left hover:border-[#7737F6] disabled:opacity-50"
          data-testid="artefact-regenerate"
        >
          <div className="flex items-center gap-2 text-[#7737F6] mb-2">
            <Sparkles size={14} />
            <span className="text-[11.5px] font-semibold tracking-[0.18em] uppercase">Re-generate</span>
          </div>
          <div className="text-[12.5px] ek-text-muted">Re-run mail-merge from the master template against the latest assessment data. Use after the score or recommendations on the run have been updated.</div>
        </button>
        <label className="border ek-line ek-surface p-4 hover:border-[#7737F6] cursor-pointer" data-testid="artefact-upload-label">
          <div className="flex items-center gap-2 text-[#7737F6] mb-2">
            <Upload size={14} />
            <span className="text-[11.5px] font-semibold tracking-[0.18em] uppercase">Upload edited version</span>
          </div>
          <div className="text-[12.5px] ek-text-muted">Edited the file locally? Upload it back here to attach it to this engagement. Keeps the same artefact ID and audit trail.</div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="artefact-upload"
          />
        </label>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Artefact detail
// ----------------------------------------------------------------------

function ArtefactDetail({ artefactId, onBack, onChange }) {
  const [artefact, setArtefact] = useState(null);
  const [markdownDraft, setMarkdownDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/artefacts/${artefactId}`);
      setArtefact(data);
      setMarkdownDraft(data.content_md || "");
    } catch {
      toast.error("Failed to load artefact");
    }
  }, [artefactId]);

  useEffect(() => { reload(); }, [reload]);

  const saveMarkdown = async (statusOverride) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/artefacts/${artefact.id}/markdown`, {
        content_md: markdownDraft,
        edit_summary: statusOverride === "final" ? "Marked final" : "Edited body",
        status: statusOverride,
      });
      setArtefact(data);
      onChange?.(data);
      toast.success(statusOverride === "final" ? "Marked final" : "Draft saved");
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!artefact) return;
    const next = artefact.status === "final" ? "draft" : "final";
    if (artefact.format === "markdown") {
      saveMarkdown(next);
      return;
    }
    try {
      const { data } = await api.patch(`/admin/artefacts/${artefact.id}/meta`, { status: next });
      setArtefact(data);
      onChange?.(data);
      toast.success(`Set to ${next}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this artefact? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/artefacts/${artefact.id}`);
      toast.success("Deleted");
      onBack();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!artefact) return <div className="ek-text-muted text-[13px] py-6">Loading…</div>;

  const isMarkdown = artefact.format === "markdown";
  const isMailMerge = MAIL_MERGE_TYPES.has(artefact.type);

  return (
    <div data-testid={`artefact-detail-${artefact.id}`}>
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-[12px] ek-text-muted hover:text-black mb-4">
        <ArrowLeft size={12} /> Back to artefacts
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#7737F6] font-semibold mb-1">
            {artefact.type}
            <span className="ek-text-muted">·</span>
            <span className="ek-text-muted">v{artefact.template_version} of master</span>
          </div>
          <h3 className="text-[18px] font-medium ek-text break-words">{artefact.title}</h3>
          <div className="text-[11.5px] ek-text-muted mt-1">
            Updated {fmtDate(artefact.updated_at)} · {artefact.created_by}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleStatus}
            className={`text-[11px] font-semibold tracking-[0.14em] uppercase px-3 py-1.5 border ${
              artefact.status === "final"
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "ek-line ek-surface ek-text-soft hover:border-emerald-300 hover:text-emerald-700"
            }`}
            data-testid="artefact-status-toggle"
          >
            {artefact.status === "final" ? <><CheckCircle2 size={11} className="inline mr-1"/>Final</> : <><Edit3 size={11} className="inline mr-1"/>Draft</>}
          </button>
          {!isMarkdown ? (
            <button
              type="button"
              onClick={() => downloadAuthed(`/admin/artefacts/${artefact.id}/download`, artefact.filename || artefact.title)}
              className="ek-btn ek-btn-outline text-[12px]"
              data-testid="artefact-download-header"
            >
              <Download size={12} /> Download
            </button>
          ) : null}
          <button type="button" onClick={remove} className="ek-text-muted hover:text-red-600" aria-label="Delete" data-testid="artefact-delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {isMarkdown ? (
        <>
          <MarkdownEditor
            value={markdownDraft}
            onChange={setMarkdownDraft}
            onSave={() => saveMarkdown()}
            saving={saving}
          />
        </>
      ) : isMailMerge ? (
        <MailMergeView artefact={artefact} onChange={(a) => { setArtefact(a); onChange?.(a); }} />
      ) : (
        <MailMergeView artefact={artefact} onChange={(a) => { setArtefact(a); onChange?.(a); }} />
      )}

      <div className="mt-6">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase ek-text-muted mb-2">
          <History size={11} /> Edit history
        </div>
        <div className="border ek-line ek-surface">
          <ul className="divide-y divide-zinc-100 text-[12.5px]">
            {(artefact.edit_history || []).slice().reverse().map((h, i) => (
              <li key={i} className="px-3 py-2 flex items-baseline gap-3" data-testid={`artefact-history-${i}`}>
                <span className="ek-text-muted text-[11px] tabular-nums w-36">{fmtDate(h.at)}</span>
                <span className="ek-text-soft flex-1">{h.summary}</span>
                <span className="ek-text-muted text-[11px]">{h.by}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Artefacts list + recommended templates
// ----------------------------------------------------------------------

export default function ArtefactsView({ runId, onBack }) {
  const [run, setRun] = useState(null);
  const [artefacts, setArtefacts] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [startingTpl, setStartingTpl] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rs }, { data: arts }, { data: recs }] = await Promise.all([
        api.get("/admin/assessments/runs"),
        api.get(`/admin/runs/${runId}/artefacts`),
        api.get(`/admin/runs/${runId}/recommended-templates`),
      ]);
      setRun(rs.items.find((r) => r.id === runId) || null);
      setArtefacts(arts.items || []);
      setRecommended(recs.items || []);
    } catch (e) {
      toast.error(`Failed to load artefacts: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  const startArtefact = async (template) => {
    setStartingTpl(template.id);
    try {
      const { data } = await api.post(`/admin/runs/${runId}/artefacts`, {
        template_id: template.id,
        auto_generate: true,
      });
      setArtefacts((prev) => [data, ...prev]);
      toast.success(`Started "${template.title}"`);
      setOpenId(data.id);
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setStartingTpl(null);
    }
  };

  const recGrouped = useMemo(() => {
    const out = {};
    for (const t of recommended) {
      const key = t.type || "Other";
      (out[key] = out[key] || []).push(t);
    }
    return out;
  }, [recommended]);

  if (openId) {
    return (
      <ArtefactDetail
        artefactId={openId}
        onBack={() => { setOpenId(null); reload(); }}
        onChange={() => reload()}
      />
    );
  }

  return (
    <div data-testid="artefacts-view">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-[12px] ek-text-muted hover:text-black mb-4">
        <ArrowLeft size={12} /> Back to runs
      </button>

      <div className="mb-6">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[#7737F6] font-semibold mb-1">Engagement artefacts</div>
        <h2 className="ek-heading text-xl">
          {run?.client_name || "Loading…"}{run?.sub_brand ? <span className="ek-text-muted"> · {run.sub_brand}</span> : null}
        </h2>
        <p className="ek-text-muted text-[13px] mt-1">Client-specific filled copies of master toolkit templates. The master catalogue is never modified by this view.</p>
      </div>

      {/* Existing artefacts */}
      <div className="mb-8">
        <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
          This engagement&apos;s artefacts ({artefacts.length})
        </h3>
        {loading ? (
          <div className="ek-text-muted text-[13px] py-6">Loading…</div>
        ) : artefacts.length === 0 ? (
          <div className="border border-dashed ek-line ek-surface-2 px-4 py-8 text-center text-[13px] ek-text-muted" data-testid="artefacts-empty">
            No artefacts yet. Start one from a recommended template below.
          </div>
        ) : (
          <div className="border ek-line ek-surface divide-y divide-zinc-100">
            {artefacts.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setOpenId(a.id)}
                className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-zinc-50"
                data-testid={`artefact-row-${a.id}`}
              >
                <FileText size={14} className="text-[#7737F6] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium ek-text truncate">{a.title}</div>
                  <div className="text-[11.5px] ek-text-muted truncate">
                    {a.type} · {a.format} · {fmtBytes(a.size_bytes)} · Updated {fmtDate(a.updated_at)}
                  </div>
                </div>
                <span className={`text-[10.5px] font-semibold tracking-[0.14em] uppercase px-2 py-0.5 ${
                  a.status === "final" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                }`}>
                  {a.status}
                </span>
                <ChevronRight size={14} className="ek-text-muted flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recommended templates */}
      <div>
        <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
          Recommended templates
        </h3>
        {Object.keys(recGrouped).length === 0 ? (
          <div className="border border-dashed ek-line ek-surface-2 px-4 py-8 text-center text-[13px] ek-text-muted" data-testid="recommended-empty">
            No matching templates in the toolkit catalogue yet. Add one via the <strong>Toolkits</strong> tab — it will appear here automatically for any run on this service.
          </div>
        ) : (
          <div className="space-y-5">
            {TYPE_ORDER.filter((t) => recGrouped[t]).map((type) => (
              <div key={type}>
                <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-2">{type}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {recGrouped[type].map((t) => (
                    <div
                      key={t.id}
                      className="border ek-line ek-surface p-4 flex flex-col"
                      data-testid={`recommended-${t.id}`}
                    >
                      <div className="text-[13px] font-semibold ek-text mb-1.5 leading-snug">{t.title}</div>
                      <div className="text-[12px] ek-text-muted leading-snug line-clamp-3 flex-1">{t.description}</div>
                      <div className="flex items-center justify-between mt-3 pt-2 border-t ek-line">
                        <span className="text-[10.5px] ek-text-muted">v{t.current_version} · {fmtBytes(t.current_size_bytes)}</span>
                        <button
                          type="button"
                          onClick={() => startArtefact(t)}
                          disabled={startingTpl === t.id}
                          className="ek-btn ek-btn-primary text-[11.5px] disabled:opacity-50"
                          data-testid={`start-artefact-${t.id}`}
                        >
                          {startingTpl === t.id ? "Starting…" : (MARKDOWN_TYPES.has(t.type) ? "Open editor" : MAIL_MERGE_TYPES.has(t.type) ? "Mail-merge" : "Start copy")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
